import type { IFundingStore } from "../interfaces/cash-deposit";

const EARTH_RADIUS_MILES = 3958.7613;

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/** Default camera when GPS is off or outside the U.S. Matches the screenbook Miami fallback. */
export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 25.7743,
  longitude: -80.1937,
};

export const isFiniteCoordinatePair = (
  lat?: number,
  lng?: number,
): lat is number =>
  lat != null &&
  lng != null &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

/**
 * Rough United States coverage (contiguous + Alaska + Hawaii).
 * Used to skip store queries for camera / GPS points that InComm cannot serve.
 */
export const isInUnitedStates = (lat: number, lng: number): boolean => {
  if (!isFiniteCoordinatePair(lat, lng)) return false;
  const contiguous = lat >= 24.5 && lat <= 49.4 && lng >= -124.8 && lng <= -66.9;
  const alaska = lat >= 51 && lat <= 71.5 && lng >= -180 && lng <= -129;
  const hawaii = lat >= 18.8 && lat <= 22.5 && lng >= -160.5 && lng <= -154.7;
  return contiguous || alaska || hawaii;
};

/** Straight-line miles. Not walking time. */
export const haversineMiles = (from: GeoPoint, to: GeoPoint): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.latitude - from.latitude);
  const dLng = toRad(to.longitude - from.longitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.latitude)) *
      Math.cos(toRad(to.latitude)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
};

/** Closest store anywhere in the catalog — used when the nearby query is empty. */
export const findNearestStore = (
  stores: IFundingStore[],
  origin: GeoPoint,
): IFundingStore | null => {
  let nearest: IFundingStore | null = null;
  let best = Number.POSITIVE_INFINITY;
  for (const store of stores) {
    if (
      !isFiniteCoordinatePair(store.address.latitude, store.address.longitude)
    ) {
      continue;
    }
    const miles = haversineMiles(origin, {
      latitude: store.address.latitude,
      longitude: store.address.longitude,
    });
    if (miles < best) {
      best = miles;
      nearest = store;
    }
  }
  return nearest;
};
